import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
} from 'react';
import { useDynamicGraphQLQuery } from '@hooks/useDynamicGraphQLQuery';
import { FilterContext } from '@components/FilterBar/FilterProvider';

interface FilterState {
  selectedLine: { id: string; label: string };
  selectedProductType: { id: string; label: string };
}

interface ValueRealizationContextType {
  filterState: FilterState;
  setSelectedLine: (line: { id: string; label: string }) => void;
  setSelectedProductType: (productType: { id: string; label: string }) => void;
  updateFilters: (filters: Partial<FilterState>) => void;
  getProcessedFilters: () => {
    lineValues: string[];
    productTypeValues: string[];
  };
  linesData: any[];
  isLoadingFilters: boolean;
}

const ValueRealizationContext = createContext<
  ValueRealizationContextType | undefined
>(undefined);

interface ValueRealizationProviderProps {
  children: ReactNode;
}

export const ValueRealizationProvider: React.FC<
  ValueRealizationProviderProps
> = ({ children }) => {
  const [filterState, setFilterState] = useState<FilterState>({
    selectedLine: { id: 'All', label: 'All Lines' },
    selectedProductType: { id: 'All', label: 'All Product Types' },
  });
  const { selectedPlant } = useContext(FilterContext);

  // Centralized API call for filter data
  const { data: ipcLocalFiltersData, isLoading: isLoadingFilters } =
    useDynamicGraphQLQuery('getIPCLocalFilters', {
      siteId: selectedPlant?.id || null,
    });

  // Get lines data from API response
  const linesData = useMemo(() => {
    if (ipcLocalFiltersData?.getIPCLocalFilters?.data?.lines) {
      return ipcLocalFiltersData.getIPCLocalFilters.data.lines;
    }
    return [];
  }, [ipcLocalFiltersData]);

  // Reset filters to 'All' when selectedPlant changes
  useEffect(() => {
    setFilterState({
      selectedLine: { id: 'All', label: 'All Lines' },
      selectedProductType: { id: 'All', label: 'All Product Types' },
    });
  }, [selectedPlant?.id]);

  const setSelectedLine = (line: { id: string; label: string }) => {
    setFilterState((prev) => ({ ...prev, selectedLine: line }));
  };

  const setSelectedProductType = (productType: {
    id: string;
    label: string;
  }) => {
    setFilterState((prev) => ({ ...prev, selectedProductType: productType }));
  };

  const updateFilters = (filters: Partial<FilterState>) => {
    setFilterState((prev) => ({ ...prev, ...filters }));
  };

  const getProcessedFilters = () => {
    const { selectedLine, selectedProductType } = filterState;

    const lineValues = [selectedLine?.id || 'All Lines'];
    const productTypeValues = [selectedProductType?.id || 'All Product Types'];

    return { lineValues, productTypeValues };
  };

  const value: ValueRealizationContextType = {
    filterState,
    setSelectedLine,
    setSelectedProductType,
    updateFilters,
    getProcessedFilters,
    linesData,
    isLoadingFilters,
  };

  return (
    <ValueRealizationContext.Provider value={value}>
      {children}
    </ValueRealizationContext.Provider>
  );
};

export const useValueRealizationContext = (): ValueRealizationContextType => {
  const context = useContext(ValueRealizationContext);
  if (context === undefined) {
    throw new Error(
      'useValueRealizationContext must be used within a ValueRealizationProvider'
    );
  }
  return context;
};
